import { FormControl, FormHelperText } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import { CSSProperties } from "@mui/styles";
import { handleKeyDown } from "core/model/accessibility/keyboardActions";
import { RadioOption } from "core/types";
import Tooltip from "ds_legacy/components/Tooltip";
import { ERROR_COLOR, SUCCESS_COLOR } from "ds_legacy/materials/colors";
import { HorizontalLayout } from "ds_legacy/materials/layouts";
import { border, margin } from "ds_legacy/materials/metrics";
import {
  FONT_SIZE_14,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_REGULAR,
} from "ds_legacy/materials/typography";
import React, { HTMLAttributes } from "react";
import {
  FormElement,
  FormElementProps,
  FormWatcher,
  isValid,
} from "react-forms-state";
import styled from "styled-components";
import { useTranslations } from "translations";
import { SubheadingFormLegend } from "../FormComponents/SubheadingFormLegend";
import { getHelperText } from "../Validation";

type RadioDisplayProps = HTMLAttributes<HTMLInputElement> & {
  "data-testid"?: string;
};

export type RadioGroupProps = {
  isAlwaysChecked?: boolean;
  isBold?: boolean;
  elementName: string;
  flex?: string;
  include?: number[] | null;
  label: string;
  labelMargin?: string;
  labelStyle?: CSSProperties;
  marginBox?: string;
  marginWrapper?: string;
  options: Array<RadioOption>;
  isRequired?: boolean;
  showOnlyActive?: boolean;
};

type RadioValue = boolean | number | string | null;

type RadioWrapperProps = {
  isAlwaysChecked?: boolean;
  isDisabled?: boolean;
  elementName?: string;
  id: string;
  label: React.ReactNode | string;
  labelMargin?: string;
  labelStyle?: CSSProperties;
  marginBox?: string;
  maxWidth?: string;
  onChange: (arg: RadioValue) => void;
  radioValue: RadioValue;
  isSuccess?: boolean;
  tooltip?: string;
  value: RadioValue | undefined;
};

const RadioBox = styled.div`
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
  flex-direction: column;
`;

const OptionsBox = styled.div<{ error?: boolean }>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: stretch;
  border-bottom: ${(props) =>
    border({ color: ERROR_COLOR, width: props.error ? 2 : 0 })};
`;

const SubForm = styled.div<{ isActive?: boolean }>`
  opacity: ${(props) => (props.isActive ? "1" : "0.7")};
  pointer-events: ${(props) => (props.isActive ? "auto" : "none")};
`;

function getFormControlLabelStyle({
  isSuccess,
  labelMargin,
  labelStyle = {},
  maxWidth,
}: Pick<
  RadioWrapperProps,
  "labelStyle" | "maxWidth" | "isSuccess" | "labelMargin"
>): CSSProperties {
  return {
    "& .MuiFormControlLabel-label": {
      ...labelStyle,
      color: isSuccess ? SUCCESS_COLOR : undefined,
      fontSize: FONT_SIZE_14,
    },
    margin: labelMargin,
    maxWidth,
  };
}

const radioStyle = {
  margin: margin(0, 1.5),
};

export function RadioWrapper({
  elementName,
  id,
  isAlwaysChecked,
  isDisabled,
  isSuccess,
  label,
  labelMargin,
  labelStyle,
  marginBox,
  maxWidth = "100%",
  onChange = () => {},
  radioValue,
  tooltip,
  value,
}: RadioWrapperProps) {
  function handleClick() {
    if (!isAlwaysChecked && value === radioValue) {
      onChange(null);
    } else {
      onChange(radioValue);
    }
  }

  const className = `radiobutton_${elementName}`;

  return (
    <FormControl
      className={className}
      data-testid={id}
      error={!isValid}
      id={id}
      sx={{
        margin: marginBox,
      }}
    >
      <Tooltip title={tooltip}>
        <FormControlLabel
          control={
            <Radio
              checked={value === radioValue}
              color="primary"
              disabled={isDisabled}
              id={`radio_${id}`}
              inputProps={{ "data-testid": `input_${id}` } as RadioDisplayProps}
              style={radioStyle}
              value={elementName}
              onChange={handleClick}
              onClick={handleClick}
            />
          }
          label={label}
          sx={getFormControlLabelStyle({
            isSuccess,
            labelMargin,
            labelStyle,
            maxWidth,
          })}
          onKeyDown={handleKeyDown({
            onConfirm: handleClick,
          })}
        />
      </Tooltip>
    </FormControl>
  );
}

export const RadioInputField = FormElement()<
  FormElementProps & RadioWrapperProps
>(RadioWrapper);

export default function RadioGroup({
  elementName,
  flex = "1 1 auto",
  include,
  isAlwaysChecked,
  isBold = false,
  isRequired,
  label,
  labelMargin,
  labelStyle,
  marginBox,
  marginWrapper,
  options,
  showOnlyActive,
}: RadioGroupProps) {
  const translations = useTranslations();
  let optionsToShow = options;

  if (include) {
    optionsToShow = options.filter((option) => include.includes(option?.value));
  }

  return (
    <FormWatcher watchPath={elementName}>
      {({ validation, watchedValidation, watchedValue }) => {
        const valid = isValid(watchedValidation);
        return (
          <FormControl
            component="fieldset"
            error={!valid}
            style={{ flex, margin: marginWrapper }}
          >
            <SubheadingFormLegend
              required={isRequired}
              sx={{
                fontWeight: isBold ? FONT_WEIGHT_BOLD : FONT_WEIGHT_REGULAR,
                ...labelStyle,
              }}
            >
              {label}
            </SubheadingFormLegend>
            <HorizontalLayout>
              <OptionsBox>
                {optionsToShow.map((option) => {
                  // Use watchedValue instead of value[elementName] because:
                  // 1. elementName can be a nested path (e.g., "patient.profile.placement_order.status")
                  // 2. value[elementName] only works for simple keys, not nested paths
                  // 3. watchedValue uses getValue() which correctly handles both simple keys and nested paths
                  const isActive = watchedValue === option.value;
                  return (
                    <RadioBox key={`radiobutton-${option.value}`}>
                      <RadioInputField
                        elementName={elementName}
                        id={`radiobutton_${elementName}_${option.id}`}
                        isAlwaysChecked={isAlwaysChecked}
                        isDisabled={option.disabled}
                        label={option.label}
                        labelMargin={labelMargin}
                        marginBox={marginBox}
                        radioValue={option.value}
                      />
                      {option.subForm && (
                        <div
                          style={{
                            display:
                              showOnlyActive && !isActive ? "none" : undefined,
                          }}
                        >
                          <SubForm isActive={isActive}>
                            {option.subForm}
                          </SubForm>
                        </div>
                      )}
                    </RadioBox>
                  );
                })}
              </OptionsBox>
            </HorizontalLayout>
            {!valid && (
              <FormHelperText>
                {getHelperText({
                  hasCustomValidation: true,
                  translations,
                  validateOverride: undefined,
                  validation,
                })}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    </FormWatcher>
  );
}
